import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronDown, FiDownload, FiRefreshCw } from 'react-icons/fi';

const tabClass = (active) => (
  `border-b-2 px-3 py-2 text-sm font-medium transition ${
    active
      ? 'border-blue-600 text-blue-700'
      : 'border-transparent text-slate-500 hover:text-slate-700'
  }`
);

const inputClass = 'rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none transition focus:border-blue-400';

export default function ReportLayout({
  title,
  description,
  viewMode,
  setViewMode,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRefresh,
  onExport,
  loading,
  basis,
  setBasis,
  showBasis = false,
  asOfOnly = false,
  showTabs = true,
  exportOpen,
  setExportOpen,
  additionalControls,
  children
}) {
  const isTransactionDetails = title === 'Transaction Details';

  return (
    <div className="mx-auto max-w-5xl pb-14">
      <header className="mb-5">
        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {title}
          </h1>
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        </div>

        <div className="flex flex-col gap-3 border-y border-slate-200 py-3">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            {showTabs ? (
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setViewMode('summary')}
                  className={tabClass(viewMode === 'summary')}
                >
                  Summary
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('details')}
                  className={tabClass(viewMode === 'details')}
                >
                  Details
                </button>
              </div>
            ) : (
              <div />
            )}

            <div className="flex flex-wrap items-center gap-2">
              {additionalControls}

              {!asOfOnly && (
                <>
                  <label className="flex items-center gap-2 text-xs text-slate-500">
                    From
                    <input
                      type="date"
                      value={startDate}
                      onChange={(event) => onStartDateChange(event.target.value)}
                      className={inputClass}
                    />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-slate-500">
                    To
                    <input
                      type="date"
                      value={endDate}
                      onChange={(event) => onEndDateChange(event.target.value)}
                      className={inputClass}
                    />
                  </label>
                </>
              )}

              {asOfOnly && (
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  As of
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => onEndDateChange(event.target.value)}
                    className={inputClass}
                  />
                </label>
              )}

              {showBasis && (
                <label className="flex items-center gap-2 text-xs text-slate-500">
                  Basis
                  <select
                    value={basis}
                    onChange={(event) => setBasis(event.target.value)}
                    className={inputClass}
                  >
                    <option value="accrual">Accrual</option>
                    <option value="cash">Cash</option>
                  </select>
                </label>
              )}

              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                aria-label="Refresh report"
                className="rounded-md border border-slate-300 bg-white p-2 text-slate-600 transition hover:border-blue-300 hover:text-blue-700 disabled:opacity-50"
              >
                <SafeIcon
                  icon={FiRefreshCw}
                  className={loading ? 'animate-spin' : ''}
                />
              </button>

              <div className="relative">
                <button
                  type="button"
                  onClick={() => setExportOpen((open) => !open)}
                  className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 transition hover:border-blue-300 hover:text-blue-700"
                >
                  <SafeIcon icon={FiDownload} />
                  Export
                  <SafeIcon icon={FiChevronDown} className="text-[11px]" />
                </button>

                {exportOpen && (
                  <div className="absolute right-0 z-30 mt-2 w-32 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg">
                    <button
                      type="button"
                      onClick={() => onExport('csv')}
                      className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                    >
                      CSV
                    </button>
                    <button
                      type="button"
                      onClick={() => onExport('pdf')}
                      className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                    >
                      PDF
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className={`relative overflow-hidden rounded-lg border border-slate-200 bg-white p-6 md:p-8 ${isTransactionDetails ? 'transaction-details-report' : ''}`}>
        {isTransactionDetails && (
          <style>{`
            .transaction-details-report ~ * {}
            [aria-label="Filter by contact"] {
              display: none;
            }
          `}</style>
        )}
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        )}
        {children}
      </main>
    </div>
  );
}